import React, { useEffect, useState, createContext, useContext } from 'react';
import { Product, Size, initialProducts } from '../data/products';
import { useLocalStorage } from '../hooks/useLocalStorage';
export interface CartItem {
  product: Product;
  size: Size;
  quantity: number;
}
export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string;
  isAdmin: boolean;
}
export type OrderStatus = 'Placed' | 'Confirmed' | 'Shipped' | 'Delivered' | 'Cancelled';
export interface OrderAddress {
  name: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
}
export interface Order {
  id: string;
  userId: string;
  items: CartItem[];
  totalAmount: number;
  status: OrderStatus;
  date: string;
  deliveryDate?: string;
  address: OrderAddress;
  paymentMethod: 'COD' | 'UPI';
}
export interface PromoCode {
  id: string;
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  expiryDate: string;
  isActive: boolean;
  applicableProductIds: string[]; // empty array = applies to all products
}
export interface AdminSettings {
  upiId: string;
  mobile: string;
  whatsappNumber: string;
  email: string;
  codEnabled: boolean;
  promoEnabled: boolean;
  notificationEnabled: boolean;
}
const defaultAdminSettings: AdminSettings = {
  upiId: 'skybrand@upi',
  mobile: '9876543210',
  whatsappNumber: '9876543210',
  email: 'support@skybrand.com',
  codEnabled: true,
  promoEnabled: true,
  notificationEnabled: true
};
interface StoreContextType {
  // Products
  products: Product[];
  addProduct: (product: Product) => void;
  editProduct: (product: Product) => void;
  deleteProduct: (id: string) => void;
  // Cart
  cart: CartItem[];
  addToCart: (product: Product, size: Size, quantity?: number) => void;
  removeFromCart: (productId: string, size: Size) => void;
  updateQuantity: (productId: string, size: Size, quantity: number) => void;
  clearCart: () => void;
  cartTotal: number;
  cartItemCount: number;
  // Wishlist
  wishlist: string[];
  toggleWishlist: (productId: string) => void;
  isInWishlist: (productId: string) => boolean;
  // Auth
  user: User | null;
  login: (email: string, password?: string) => boolean;
  logout: () => void;
  signup: (name: string, email: string, phone?: string) => void;
  // Orders
  orders: Order[];
  placeOrder: (address: OrderAddress, paymentMethod?: 'COD' | 'UPI') => string;
  cancelOrder: (orderId: string) => void;
  updateOrderStatus: (orderId: string, status: OrderStatus, deliveryDate?: string) => void;
  getUserOrders: () => Order[];
  // Search & Filter
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // Promo Codes
  promoCodes: PromoCode[];
  addPromoCode: (promo: PromoCode) => void;
  editPromoCode: (promo: PromoCode) => void;
  deletePromoCode: (id: string) => void;
  getApplicablePromo: (productId: string) => PromoCode | undefined;
  validatePromoCode: (code: string, cartItems: CartItem[]) => {
    isValid: boolean;
    discount: number;
    message: string;
  };
  // Admin Settings
  adminSettings: AdminSettings;
  updateAdminSettings: (settings: AdminSettings) => void;
}
const StoreContext = createContext<StoreContextType | undefined>(undefined);
export function StoreProvider({
  children
}: {
  children: ReactNode;
}) {
  // State
  const [products, setProducts] = useLocalStorage<Product[]>('skybrand_products', initialProducts);
  const [cart, setCart] = useLocalStorage<CartItem[]>('skybrand_cart', []);
  const [wishlist, setWishlist] = useLocalStorage<string[]>('skybrand_wishlist', []);
  const [user, setUser] = useLocalStorage<User | null>('skybrand_user', null);
  const [orders, setOrders] = useLocalStorage<Order[]>('skybrand_orders', []);
  const [searchQuery, setSearchQuery] = useState('');
  const [promoCodes, setPromoCodes] = useLocalStorage<PromoCode[]>('skybrand_promos', []);
  const [adminSettings, setAdminSettings] = useLocalStorage<AdminSettings>('skybrand_settings', defaultAdminSettings);
  // Derived state
  const cartTotal = cart.reduce((total, item) => total + item.product.sellingPrice * item.quantity, 0);
  const cartItemCount = cart.reduce((count, item) => count + item.quantity, 0);
  // Product Actions
  const addProduct = (product: Product) => setProducts([...products, product]);
  const editProduct = (updatedProduct: Product) => {
    setProducts(products.map(p => p.id === updatedProduct.id ? updatedProduct : p));
    setCart(cart.map(item => item.product.id === updatedProduct.id ? {
      ...item,
      product: updatedProduct
    } : item));
  };
  const deleteProduct = (id: string) => {
    setProducts(products.filter(p => p.id !== id));
    setCart(cart.filter(item => item.product.id !== id));
    setWishlist(wishlist.filter(wId => wId !== id));
  };
  // Cart Actions
  const addToCart = (product: Product, size: Size, quantity = 1) => {
    setCart(prevCart => {
      const existingItemIndex = prevCart.findIndex(item => item.product.id === product.id && item.size === size);
      if (existingItemIndex >= 0) {
        const newCart = [...prevCart];
        newCart[existingItemIndex].quantity += quantity;
        return newCart;
      }
      return [...prevCart, {
        product,
        size,
        quantity
      }];
    });
  };
  const removeFromCart = (productId: string, size: Size) => {
    setCart(prevCart => prevCart.filter(item => !(item.product.id === productId && item.size === size)));
  };
  const updateQuantity = (productId: string, size: Size, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }
    setCart(prevCart => prevCart.map(item => item.product.id === productId && item.size === size ? {
      ...item,
      quantity
    } : item));
  };
  const clearCart = () => setCart([]);
  // Wishlist Actions
  const toggleWishlist = (productId: string) => {
    setWishlist(prev => prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]);
  };
  const isInWishlist = (productId: string) => wishlist.includes(productId);
  // Auth Actions
  const login = (email: string, password?: string) => {
    if (email === 'admin@skybrand.com' && password === 'admin123') {
      setUser({
        id: 'admin_1',
        name: 'Admin User',
        email: 'admin@skybrand.com',
        isAdmin: true
      });
      return true;
    }
    if (email && !email.includes('admin')) {
      setUser({
        id: `user_${Date.now()}`,
        name: email.split('@')[0],
        email: email,
        isAdmin: false
      });
      return true;
    }
    return false;
  };
  const logout = () => setUser(null);
  const signup = (name: string, email: string, phone?: string) => {
    setUser({
      id: `user_${Date.now()}`,
      name,
      email,
      phone,
      isAdmin: false
    });
  };
  // Order Actions
  const placeOrder = (address: OrderAddress, paymentMethod: 'COD' | 'UPI' = 'COD') => {
    if (!user) throw new Error('Must be logged in to place an order');
    if (cart.length === 0) throw new Error('Cart is empty');
    // Calculate final total including promo (simplified for placeOrder, actual calculation happens in Checkout)
    // We'll just use cartTotal here and assume the checkout page passes the correct total if needed,
    // but for simplicity we'll just store the cartTotal. A real app would pass the final amount.
    const newOrder: Order = {
      id: `SKY-${Date.now()}`,
      userId: user.id,
      items: [...cart],
      totalAmount: cartTotal,
      status: 'Placed',
      date: new Date().toISOString(),
      address,
      paymentMethod
    };
    setOrders([newOrder, ...orders]);
    // Decrease stock
    const updatedProducts = [...products];
    cart.forEach(cartItem => {
      const productIndex = updatedProducts.findIndex(p => p.id === cartItem.product.id);
      if (productIndex >= 0) {
        if (updatedProducts[productIndex].sizes[cartItem.size] >= cartItem.quantity) {
          updatedProducts[productIndex].sizes[cartItem.size] -= cartItem.quantity;
        }
      }
    });
    setProducts(updatedProducts);
    clearCart();
    return newOrder.id;
  };
  const cancelOrder = (orderId: string) => {
    setOrders(orders.map(order => {
      if (order.id === orderId && (order.status === 'Placed' || order.status === 'Confirmed')) {
        const updatedProducts = [...products];
        order.items.forEach(item => {
          const productIndex = updatedProducts.findIndex(p => p.id === item.product.id);
          if (productIndex >= 0) {
            updatedProducts[productIndex].sizes[item.size] += item.quantity;
          }
        });
        setProducts(updatedProducts);
        return {
          ...order,
          status: 'Cancelled'
        };
      }
      return order;
    }));
  };
  const updateOrderStatus = (orderId: string, status: OrderStatus, deliveryDate?: string) => {
    setOrders(orders.map(order => {
      if (order.id === orderId) {
        return {
          ...order,
          status,
          ...(deliveryDate ? {
            deliveryDate
          } : {})
        };
      }
      return order;
    }));
  };
  const getUserOrders = () => {
    if (!user) return [];
    return orders.filter(order => order.userId === user.id);
  };
  // Promo Code Actions
  const addPromoCode = (promo: PromoCode) => setPromoCodes([...promoCodes, promo]);
  const editPromoCode = (updatedPromo: PromoCode) => {
    setPromoCodes(promoCodes.map(p => p.id === updatedPromo.id ? updatedPromo : p));
  };
  const deletePromoCode = (id: string) => setPromoCodes(promoCodes.filter(p => p.id !== id));
  const getApplicablePromo = (productId: string) => {
    if (!adminSettings.promoEnabled) return undefined;
    const now = new Date().toISOString();
    return promoCodes.find(p => p.isActive && p.expiryDate > now && (p.applicableProductIds.length === 0 || p.applicableProductIds.includes(productId)));
  };
  const validatePromoCode = (code: string, cartItems: CartItem[]) => {
    if (!adminSettings.promoEnabled) {
      return {
        isValid: false,
        discount: 0,
        message: 'Promo codes are currently disabled'
      };
    }
    const promo = promoCodes.find(p => p.code.toUpperCase() === code.toUpperCase());
    if (!promo) return {
      isValid: false,
      discount: 0,
      message: 'Invalid promo code'
    };
    if (!promo.isActive) return {
      isValid: false,
      discount: 0,
      message: 'This promo code is no longer active'
    };
    if (new Date(promo.expiryDate) < new Date()) return {
      isValid: false,
      discount: 0,
      message: 'This promo code has expired'
    };
    // Calculate discount
    let applicableTotal = 0;
    if (promo.applicableProductIds.length === 0) {
      // Applies to all products
      applicableTotal = cartItems.reduce((sum, item) => sum + item.product.sellingPrice * item.quantity, 0);
    } else {
      // Applies to specific products
      const applicableItems = cartItems.filter(item => promo.applicableProductIds.includes(item.product.id));
      if (applicableItems.length === 0) {
        return {
          isValid: false,
          discount: 0,
          message: 'This promo code is not applicable to any items in your cart'
        };
      }
      applicableTotal = applicableItems.reduce((sum, item) => sum + item.product.sellingPrice * item.quantity, 0);
    }
    let discountAmount = 0;
    if (promo.discountType === 'percentage') {
      discountAmount = Math.round(applicableTotal * (promo.discountValue / 100));
    } else {
      discountAmount = Math.min(promo.discountValue, applicableTotal); // Can't discount more than the total
    }
    return {
      isValid: true,
      discount: discountAmount,
      message: `Promo code applied! You saved ₹${discountAmount}`
    };
  };
  // Admin Settings Actions
  const updateAdminSettings = (settings: AdminSettings) => setAdminSettings(settings);
  return <StoreContext.Provider value={{
    products,
    addProduct,
    editProduct,
    deleteProduct,
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartItemCount,
    wishlist,
    toggleWishlist,
    isInWishlist,
    user,
    login,
    logout,
    signup,
    orders,
    placeOrder,
    cancelOrder,
    updateOrderStatus,
    getUserOrders,
    searchQuery,
    setSearchQuery,
    promoCodes,
    addPromoCode,
    editPromoCode,
    deletePromoCode,
    getApplicablePromo,
    validatePromoCode,
    adminSettings,
    updateAdminSettings
  }} data-id="element-411">
      {children}
    </StoreContext.Provider>;
}
export function useStore() {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}